import React, { useState, useEffect, useCallback } from 'react';
import { getApi, postApi } from '../../services/api';
import { toast } from 'react-toastify';

/* ──────────────────────────────────────────────────────────────────────────
   API response (getTransfers):
   data.available_amount → user's current available balance
   data.sent_amount      → total amount sent
   data.receive_amount   → total amount received
   data.transfers[]      → list of transactions
   data.total / current_page / per_page → pagination
────────────────────────────────────────────────────────────────────────── */

const Transfer = () => {
  /* ── State ── */
  const [transfers,        setTransfers]        = useState([]);
  const [totalCount,       setTotalCount]       = useState(0);
  const [currentPage,      setCurrentPage]      = useState(1);
  const [loading,          setLoading]          = useState(true);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [sentAmount,       setSentAmount]       = useState(0);
  const [receiveAmount,    setReceiveAmount]    = useState(0);
  const [myUserId,         setMyUserId]         = useState(null);

  /* Modal */
  const [showModal,    setShowModal]    = useState(false);
  const [receiverId,   setReceiverId]   = useState('');   // receiver_id (user ID)
  const [amount,       setAmount]       = useState('');
  const [password,     setPassword]     = useState('');   // transaction password
  const [showPassword, setShowPassword] = useState(false);
  const [submitting,   setSubmitting]   = useState(false);

  /* Filter tab */
  const [activeTab, setActiveTab] = useState('all');

  const PER_PAGE = 10;

  /* ── Fetch profile (only for userId for sent/received detection) ── */
  const fetchProfile = useCallback(async () => {
    try {
      const res  = await getApi('profile');
      const user = res.data?.USER || res.data?.data;
      if (user) setMyUserId(String(user.id));
    } catch (e) {
      console.error('Profile fetch error:', e);
    }
  }, []);

  /* ── Fetch transfer history (includes balance totals) ── */
  const fetchTransfers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getApi('getTransfers', { page, per_page: PER_PAGE });
      if (res.data?.status === true) {
        const d = res.data.data;
        setTransfers(d.transfers || []);
        setTotalCount(d.total || 0);
        setCurrentPage(d.current_page || 1);
        setAvailableBalance(parseFloat(d.available_amount || 0));
        setSentAmount(parseFloat(d.sent_amount || 0));
        setReceiveAmount(parseFloat(d.receive_amount || 0));
      } else {
        toast.error(res.data?.message || 'Failed to fetch transfers');
      }
    } catch (e) {
      console.error('Transfer fetch error:', e);
      toast.error('Failed to load transfer history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchTransfers(1);
  }, [fetchProfile, fetchTransfers]);

  /* ── Determine sent/received using myUserId ── */
  const resolveType = (tx) =>
    myUserId && String(tx.user_id) === myUserId ? 'sent' : 'received';

  /* ── Filter ── */
  const filtered = transfers.filter(tx => {
    const type = resolveType(tx);
    if (activeTab === 'sent')     return type === 'sent';
    if (activeTab === 'received') return type === 'received';
    return true;
  });

  /* ── Handle transfer submit ── */
  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!receiverId.trim())                       { toast.error('Please enter the recipient ID.'); return; }
    if (!amount || Number(amount) <= 0)           { toast.error('Please enter a valid amount.'); return; }
    if (Number(amount) > availableBalance)        { toast.error('Insufficient balance.'); return; }
    if (!password.trim())                         { toast.error('Please enter your transaction password.'); return; }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('receiver_id', receiverId.trim());
      formData.append('amount',      amount);
      formData.append('password',    password);

      const res = await postApi('transfer', formData);
      if (res.data?.status === true) {
        toast.success(res.data?.message || `Successfully transferred $${Number(amount).toFixed(2)}`);
        closeModal();
        fetchTransfers(1);
        fetchProfile();
      } else {
        toast.error(res.data?.message || 'Transfer failed. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openModal  = () => setShowModal(true);
  const closeModal = () => {
    if (!submitting) { setShowModal(false); setReceiverId(''); setAmount(''); setPassword(''); setShowPassword(false); }
  };

  const totalPages = Math.ceil(totalCount / PER_PAGE);

  /* ─────────────────────────── RENDER ─────────────────────────── */
  return (
    <div className="page-wrapper">

      {/* ── Header ── */}
      <div className="page-header mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <div className="section-label">Finance</div>
          <h2 className="page-title">Transfer Asset</h2>
          <p className="page-subtitle">Send and receive assets with other users instantly</p>
        </div>
        <button
          className="btn btn-gradient"
          onClick={openModal}
          style={{ borderRadius: 12, padding: '0.7rem 1.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <i className="bi bi-send"></i> Transfer
        </button>
      </div>

      {/* ── Summary Cards ── */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Available Balance', value: `$${parseFloat(availableBalance).toFixed(3)}`, icon: 'bi-wallet2',               color: 'var(--page-accent)', bg: 'rgba(0,255,149,0.08)',  border: 'rgba(0,255,149,0.2)'  },
          { label: 'Total Sent',        value: `$${parseFloat(sentAmount).toFixed(3)}`,      icon: 'bi-arrow-up-circle-fill',  color: 'var(--page-danger)', bg: 'rgba(255,77,109,0.08)', border: 'rgba(255,77,109,0.2)' },
          { label: 'Total Received',    value: `$${parseFloat(receiveAmount).toFixed(3)}`,   icon: 'bi-arrow-down-circle-fill', color: '#00d4ff',            bg: 'rgba(0,212,255,0.08)',  border: 'rgba(0,212,255,0.2)'  },
          { label: 'Transactions',      value: totalCount,                                    icon: 'bi-arrow-left-right',      color: '#b57bee',            bg: 'rgba(123,47,255,0.08)', border: 'rgba(123,47,255,0.2)' },
        ].map((card, i) => (
          <div key={i} className="col-6 col-lg-3">
            <div className="p-card" style={{ border: `1px solid ${card.border}`, background: card.bg }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: card.bg, border: `1px solid ${card.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={`bi ${card.icon}`} style={{ color: card.color, fontSize: '1.2rem' }}></i>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>{card.label}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: card.color, fontFamily: 'JetBrains Mono, monospace' }}>
                    {loading ? <span className="skeleton d-inline-block" style={{ width: 70, height: 20 }}></span> : card.value}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Transfer History ── */}
      <div className="p-card">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <p className="section-label mb-0">Transfer History</p>
          <div className="p-tabs" style={{ marginBottom: 0 }}>
            {['all', 'sent', 'received'].map(tab => (
              <button
                key={tab}
                className={`p-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
                style={{ textTransform: 'capitalize' }}
              >
                {tab === 'all'      && <i className="bi bi-list-ul me-1"></i>}
                {tab === 'sent'     && <i className="bi bi-arrow-up-circle me-1"></i>}
                {tab === 'received' && <i className="bi bi-arrow-down-circle me-1"></i>}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success mb-3" style={{ width: '2rem', height: '2rem' }}></div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Loading transfers...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>
            <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
            <p className="mt-2" style={{ fontSize: '0.9rem' }}>No transactions found</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filtered.map(tx => {
              const type        = resolveType(tx);
              const isSent      = type === 'sent';
              const counterUser = isSent ? tx.receiver_name : tx.user_name;
              const counterId   = isSent ? tx.receiver_id   : tx.user_id;

              return (
                <div
                  key={tx.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem 1.1rem',
                    borderRadius: 14,
                    background: isSent ? 'rgba(255,77,109,0.04)' : 'rgba(0,212,255,0.04)',
                    border:     isSent ? '1px solid rgba(255,77,109,0.15)' : '1px solid rgba(0,212,255,0.15)',
                    transition: 'transform 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}
                >
                  {/* Icon + Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      background: isSent ? 'rgba(255,77,109,0.12)' : 'rgba(0,212,255,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i
                        className={`bi ${isSent ? 'bi-arrow-up-right' : 'bi-arrow-down-left'}`}
                        style={{ color: isSent ? 'var(--page-danger)' : '#00d4ff', fontSize: '1.1rem' }}
                      ></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                        {isSent ? 'Sent to' : 'Received from'}{' '}
                        <span style={{ color: isSent ? 'var(--page-danger)' : '#00d4ff' }}>{counterUser}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', marginRight: 8 }}>
                          ID: {counterId}
                        </span>
                        <span>· {tx.date_time}</span>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        Balance after: <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>${parseFloat(tx.remain_amount).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Amount + Badge */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontWeight: 800, fontSize: '1rem',
                      fontFamily: 'JetBrains Mono, monospace',
                      color: isSent ? 'var(--page-danger)' : '#00d4ff',
                    }}>
                      {isSent ? '−' : '+'} ${parseFloat(tx.transfer_amount).toFixed(2)}
                    </div>
                    <span
                      className={`s-pill ${tx.status === 'success' ? 'active' : 'pending'}`}
                      style={{ fontSize: '0.65rem', marginTop: 4, display: 'inline-block' }}
                    >
                      {tx.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
            <button
              className="btn btn-sm"
              disabled={currentPage <= 1 || loading}
              onClick={() => fetchTransfers(currentPage - 1)}
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'white', borderRadius: 8 }}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Page <strong style={{ color: 'white' }}>{currentPage}</strong> of <strong style={{ color: 'white' }}>{totalPages}</strong>
            </span>
            <button
              className="btn btn-sm"
              disabled={currentPage >= totalPages || loading}
              onClick={() => fetchTransfers(currentPage + 1)}
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'white', borderRadius: 8 }}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        )}
      </div>

      {/* ══ Transfer Modal ══ */}
      {showModal && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
            zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem', animation: 'fadeInOverlay 0.2s ease',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(10,26,20,0.98), rgba(8,18,32,0.98))',
              border: '1px solid rgba(0,255,149,0.2)', borderRadius: 24, padding: '2rem',
              width: '100%', maxWidth: 480, position: 'relative',
              boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 40px rgba(0,255,149,0.06)',
              animation: 'slideUpModal 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            {/* Close */}
            <button onClick={closeModal} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, width: 32, height: 32, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="bi bi-x-lg" style={{ fontSize: '0.85rem' }}></i>
            </button>

            {/* Header */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(0,255,149,0.1)', border: '1px solid rgba(0,255,149,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                <i className="bi bi-send-fill" style={{ color: 'var(--page-accent)', fontSize: '1.3rem' }}></i>
              </div>
              <h5 style={{ fontWeight: 800, marginBottom: 4 }}>Transfer Asset</h5>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>Send funds to another user using their code</p>
            </div>

            {/* Balance */}
            <div style={{ background: 'rgba(0,255,149,0.06)', border: '1px solid rgba(0,255,149,0.15)', borderRadius: 12, padding: '0.75rem 1rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                <i className="bi bi-wallet2 me-1" style={{ color: 'var(--page-accent)' }}></i>Available Balance
              </span>
              <span style={{ fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: 'var(--page-accent)' }}>
                ${availableBalance.toFixed(2)}
              </span>
            </div>

            {/* Form */}
            <form onSubmit={handleTransfer}>
              {/* Receiver ID */}
              <div className="mb-3">
                <label className="p-label">Receiver ID</label>
                <div className="address-box" style={{ borderRadius: 12 }}>
                  <i className="bi bi-person-badge" style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}></i>
                  <input
                    type="text"
                    value={receiverId}
                    onChange={e => setReceiverId(e.target.value)}
                    placeholder="Enter receiver user ID"
                    style={{ background: 'transparent', border: 'none', color: 'white', flex: 1, outline: 'none', fontSize: '0.9rem' }}
                  />
                </div>
              </div>

              {/* Amount */}
              <div className="mb-3">
                <label className="p-label">Amount (USDT)</label>
                <div className="address-box" style={{ borderRadius: 12 }}>
                  <i className="bi bi-currency-dollar" style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}></i>
                  <input
                    type="number" value={amount} onChange={e => setAmount(e.target.value)}
                    placeholder="0.00" min="1" step="0.01"
                    style={{ background: 'transparent', border: 'none', color: 'white', flex: 1, outline: 'none', fontSize: '0.9rem' }}
                  />
                  <button type="button" onClick={() => setAmount(availableBalance.toString())}
                    style={{ background: 'rgba(0,255,149,0.1)', border: '1px solid rgba(0,255,149,0.25)', borderRadius: 6, padding: '2px 10px', color: 'var(--page-accent)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                    MAX
                  </button>
                </div>
                {amount && Number(amount) > availableBalance && (
                  <p style={{ color: 'var(--page-danger)', fontSize: '0.75rem', marginTop: 4 }}>
                    <i className="bi bi-exclamation-circle me-1"></i>Exceeds available balance
                  </p>
                )}
              </div>

              {/* Transaction Password */}
              <div className="mb-4">
                <label className="p-label">Transaction Password</label>
                <div className="address-box" style={{ borderRadius: 12 }}>
                  <i className="bi bi-shield-lock" style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}></i>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    style={{ background: 'transparent', border: 'none', color: 'white', flex: 1, outline: 'none', fontSize: '0.9rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', padding: '0 6px', flexShrink: 0 }}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                  </button>
                </div>
              </div>

              {/* Summary */}
              {receiverId && amount && Number(amount) > 0 && (
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Receiver ID</span>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: 'var(--page-accent)' }}>{receiverId}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>You pay</span>
                    <span style={{ fontSize: '0.9rem', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: 'var(--page-danger)' }}>−${Number(amount).toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={closeModal} disabled={submitting}
                  style={{ flex: 1, padding: '0.75rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-gradient"
                  disabled={submitting || !receiverId.trim() || !amount || Number(amount) <= 0 || !password.trim()}
                  style={{ flex: 2, borderRadius: 12, padding: '0.75rem', fontWeight: 700 }}>
                  {submitting
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                    : <><i className="bi bi-send me-2"></i>Confirm Transfer</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInOverlay { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUpModal  { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default Transfer;
